import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import attendanceService from '../services/attendanceService';
import attendanceCorrectionService from '../services/attendanceCorrectionService';

const AttendanceCorrectionPage = () => {
  const { user } = useAuth();
  const canAccessAttendanceCorrection = ['HRO', 'CHAIRMAN'].includes(user?.role);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allCorrectionRequests, setAllCorrectionRequests] = useState([]);
  const [hrEmployees, setHrEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoadingId, setReviewLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [filters, setFilters] = useState({
    hrId: '',
    date: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: ''
  });

  const normalizeValue = (value) => (value ? String(value).trim() : '');

  const getEmployeeKey = (employee) => normalizeValue(employee?._id || employee?.id || employee?.userId);

  const getEmployeeName = (employee) => {
    const fullName = normalizeValue(employee?.fullName);
    const name = normalizeValue(employee?.name);
    const firstName = normalizeValue(employee?.firstName);
    const lastName = normalizeValue(employee?.lastName);

    if (fullName) return fullName;
    if (name) return name;
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();

    return '';
  };

  const getEmployeeCode = (employee) => {
    const employeeId = normalizeValue(employee?.employeeId);
    const jobId = normalizeValue(employee?.jobId);
    const username = normalizeValue(employee?.username);

    return employeeId || jobId || username || '';
  };

  const employeeDirectory = useMemo(() => {
    const directory = new Map();

    hrEmployees.forEach((employee) => {
      const key = getEmployeeKey(employee);
      if (key) {
        directory.set(key, employee);
      }
    });

    return directory;
  }, [hrEmployees]);

  const anonymousEmployeeLabels = useMemo(() => {
    const labels = new Map();
    let counter = 1;

    const assignIfAnonymous = (employee) => {
      if (!employee) return;

      const key = getEmployeeKey(employee);
      if (!key) return;

      const mappedEmployee = employeeDirectory.get(key) || employee;
      const hasName = Boolean(getEmployeeName(mappedEmployee));
      const hasEmployeeCode = Boolean(getEmployeeCode(mappedEmployee));

      if (hasName || hasEmployeeCode) return;

      if (!labels.has(key)) {
        labels.set(key, `employee${counter}`);
        counter += 1;
      }
    };

    hrEmployees.forEach((employee) => assignIfAnonymous(employee));
    attendanceRecords.forEach((record) => assignIfAnonymous(record?.hrId));
    pendingRequests.forEach((request) => assignIfAnonymous(request?.requestedBy));
    allCorrectionRequests.forEach((request) => assignIfAnonymous(request?.requestedBy));

    return labels;
  }, [attendanceRecords, pendingRequests, allCorrectionRequests, hrEmployees, employeeDirectory]);

  const getEmployeeIdentity = (employee) => {
    const key = getEmployeeKey(employee);
    const mappedEmployee = (key && employeeDirectory.get(key)) || employee || {};

    const name = getEmployeeName(mappedEmployee);
    const employeeCode = getEmployeeCode(mappedEmployee);

    if (name || employeeCode) {
      return {
        primary: name || `Employee ${employeeCode}`,
        secondary: employeeCode || 'N/A'
      };
    }

    return {
      primary: (key && anonymousEmployeeLabels.get(key)) || 'employee',
      secondary: 'N/A'
    };
  };

  useEffect(() => {
    if (canAccessAttendanceCorrection) {
      fetchData();
    }
  }, [canAccessAttendanceCorrection, filters, requestStatusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [attendanceResponse, employeesResponse, pendingRequestsResponse, allRequestsResponse] = await Promise.all([
        attendanceService.getAllHRAttendance(filters),
        attendanceService.getHREmployeesList(),
        attendanceCorrectionService.getPendingRequests(),
        attendanceCorrectionService.getHROCorrectionRequests(requestStatusFilter)
      ]);
      
      if (attendanceResponse.success) {
        setAttendanceRecords(attendanceResponse.data.records);
      }
      
      if (employeesResponse.success) {
        setHrEmployees(employeesResponse.data);
      }

      if (pendingRequestsResponse.success) {
        setPendingRequests(pendingRequestsResponse.data || []);
      }

      if (allRequestsResponse.success) {
        setAllCorrectionRequests(allRequestsResponse.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getRequestedChangeSummary = (request) => {
    const changes = [];
    if (request?.requestedChanges?.punchIn) changes.push('Punch In');
    if (request?.requestedChanges?.punchOut) changes.push('Punch Out');
    if (request?.requestedChanges?.status) changes.push(`Status: ${request.requestedChanges.status}`);
    if (request?.requestedChanges?.remarks) changes.push('Remarks');
    return changes.length > 0 ? changes.join(', ') : 'No field changes';
  };

  const handleReviewRequest = async (requestId, action) => {
    try {
      setError('');
      setSuccess('');

      const comments = window.prompt(
        action === 'approve'
          ? 'Optional comments for approval:'
          : 'Reason for rejection (required):',
        ''
      );

      if (comments === null) {
        return;
      }

      if (action === 'reject' && !comments.trim()) {
        setError('Rejection comment is required.');
        return;
      }

      setReviewLoadingId(requestId);
      const response = await attendanceCorrectionService.reviewCorrectionRequest(
        requestId,
        action,
        comments.trim()
      );

      if (response.success) {
        setSuccess(response.message || `Request ${action}d successfully`);
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to review correction request');
    } finally {
      setReviewLoadingId(null);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status, detailedStatus) => {
    // Use detailed status for display if available
    const displayStatus = detailedStatus?.displayStatus || status;
    
    const colors = {
      'Present': 'bg-green-100 text-green-800 border-green-200',
      'Late': 'bg-orange-100 text-orange-800 border-orange-200',
      'Late In': 'bg-orange-100 text-orange-800 border-orange-200',
      'Early Out': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Late In & Early Out': 'bg-red-100 text-red-800 border-red-200',
      'Absent': 'bg-red-100 text-red-800 border-red-200',
      'HalfDay': 'bg-blue-100 text-blue-800 border-blue-200',
      'OnLeave': 'bg-purple-100 text-purple-800 border-purple-200',
      'EarlyOut': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[displayStatus] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRequestStatusColor = (status) => {
    const colors = {
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Approved: 'bg-green-100 text-green-800 border-green-200',
      Rejected: 'bg-red-100 text-red-800 border-red-200'
    };

    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!canAccessAttendanceCorrection) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HRO and Chairman.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Attendance Correction</h1>
            <p className="text-gray-600">Review and correct HR employee attendance records</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
              <select
                value={filters.hrId}
                onChange={(e) => setFilters({ ...filters, hrId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Employees</option>
                {hrEmployees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {(() => {
                      const identity = getEmployeeIdentity(emp);
                      return `${identity.primary} (${identity.secondary})`;
                    })()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Specific Date</label>
              <input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <select
                value={filters.month}
                onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <select
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Present">Present</option>
                <option value="Late">Late</option>
                <option value="HalfDay">Half Day</option>
                <option value="Absent">Absent</option>
                <option value="OnLeave">On Leave</option>
                <option value="EarlyOut">Early Out</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pending Correction Requests */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Pending Correction Requests</h2>
            <p className="text-sm text-gray-500 mt-1">
              Review HR-submitted correction requests assigned to you.
            </p>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading requests...</p>
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No pending correction requests.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Changes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Punch In</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Punch Out</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingRequests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const identity = getEmployeeIdentity(request.requestedBy);
                          return (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {identity.primary}
                              </div>
                              <div className="text-sm text-gray-500">
                                {identity.secondary}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.date}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{getRequestedChangeSummary(request)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {request?.requestedChanges?.punchIn ? formatDateTime(request.requestedChanges.punchIn) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {request?.requestedChanges?.punchOut ? formatDateTime(request.requestedChanges.punchOut) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs break-words">{request.reason}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                          {request.priority || 'Medium'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDateTime(request.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleReviewRequest(request._id, 'approve')}
                            disabled={reviewLoadingId === request._id}
                            className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReviewRequest(request._id, 'reject')}
                            disabled={reviewLoadingId === request._id}
                            className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Attendance Records */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Attendance Records</h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading records...</p>
            </div>
          ) : attendanceRecords.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No attendance records found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punch In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punch Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modified
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceRecords.map((record) => (
                    <tr key={record._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const identity = getEmployeeIdentity(record.hrId);
                          return (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {identity.primary}
                              </div>
                              <div className="text-sm text-gray-500">
                                {identity.secondary}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{record.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(record.punchIn)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(record.punchOut)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {record.totalHours ? `${record.totalHours}h` : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status, record.detailedStatus)}`}>
                          {record.detailedStatus?.displayStatus || record.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.modifiedBy ? (
                          <div className="text-xs text-gray-500">
                            <div>Modified</div>
                            <div>{formatDateTime(record.modifiedAt)}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Original</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Correction Request History */}
        <div className="bg-white rounded-lg shadow-md mb-6 mt-6">
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Correction Request History</h2>
              <p className="text-sm text-gray-500 mt-1">
                View all attendance correction requests with Pending, Approved, and Rejected statuses.
              </p>
            </div>

            <div className="w-full md:w-56">
              <label className="block text-xs font-medium text-gray-600 mb-1">Request Status</label>
              <select
                value={requestStatusFilter}
                onChange={(e) => setRequestStatusFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading request history...</p>
            </div>
          ) : allCorrectionRequests.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No correction requests found for selected status.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Changes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied On</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewed By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {allCorrectionRequests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const identity = getEmployeeIdentity(request.requestedBy);
                          return (
                            <>
                              <div className="text-sm font-medium text-gray-900">{identity.primary}</div>
                              <div className="text-sm text-gray-500">{identity.secondary}</div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{request.date}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{getRequestedChangeSummary(request)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs break-words">{request.reason}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                          {request.priority || 'Medium'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRequestStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDateTime(request.createdAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {(() => {
                          const reviewer = request?.response?.reviewedBy;
                          if (!reviewer) return '-';
                          const identity = getEmployeeIdentity(reviewer);
                          return `${identity.primary}`;
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {request.status === 'Pending' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleReviewRequest(request._id, 'approve')}
                              disabled={reviewLoadingId === request._id}
                              className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReviewRequest(request._id, 'reject')}
                              disabled={reviewLoadingId === request._id}
                              className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Reviewed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default AttendanceCorrectionPage;